import { useState, useRef, useEffect, useCallback } from "react";
import { MessageSquare, X, Send, Loader2, Sparkles, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WELCOME =
  "Dobrý den, jsem Eva, online asistentka EffectDeal. 👋 Poradím vám s výběrem vozu, cenami i dostupností. Co hledáte?";
const SUGGESTIONS = [
  "Jaké vozy máte k dispozici?",
  "Potřebuju SUV pro rodinu",
  "Co je v ceně pronájmu?",
];

// Floating public AI assistant (Eva) – answers about cars/prices, recommends, captures leads to CRM.
const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ name: "", contact: "" });
  const [leadSending, setLeadSending] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [leadErr, setLeadErr] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => { if (open) scrollToEnd(); }, [messages, open, loading, showLead, scrollToEnd]);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.filter((m) => m.role === "user" || m.role === "assistant");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg, history }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply || "..." }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Omlouvám se, teď se mi nedaří odpovědět. Zkuste to prosím za chvíli, nebo nám zanechte kontakt a ozveme se." }]);
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.contact.trim()) { setLeadErr("Zadejte prosím telefon nebo e-mail."); return; }
    setLeadSending(true); setLeadErr(null);
    try {
      const transcript = messages.map((m) => `${m.role === "user" ? "Zákazník" : "Eva"}: ${m.content}`).join("\n");
      const res = await fetch(`${BACKEND_URL}/api/chat/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: lead.name, contact: lead.contact, transcript }),
      });
      if (!res.ok) throw new Error();
      setLeadDone(true);
      setMessages((m) => [...m, { role: "assistant", content: "Děkujeme! Máme váš kontakt a brzy se vám ozveme. 🚗" }]);
      setTimeout(() => setShowLead(false), 1800);
    } catch {
      setLeadErr("Odeslání se nezdařilo, zkuste to prosím znovu.");
    } finally {
      setLeadSending(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Zeptat se asistentky"
        data-testid="chat-launcher"
        className="fixed right-4 sm:right-5 bottom-[88px] sm:bottom-5 z-[60] w-14 h-14 rounded-full bg-orange-500 text-black shadow-lg shadow-orange-500/30 flex items-center justify-center hover:scale-110 active:scale-95 transition-transform duration-200"
      >
        {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
        {!open && <span className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-[#050505] animate-pulse" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-[60] bg-[#0b0b0d] border border-white/10 shadow-2xl shadow-black/50 flex flex-col overflow-hidden
                     inset-x-2 bottom-[150px] top-16 rounded-3xl
                     sm:inset-x-auto sm:top-auto sm:right-5 sm:bottom-24 sm:w-[380px] sm:h-[560px] animate-fade-in"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-400 text-black shrink-0">
            <div className="w-9 h-9 rounded-full bg-black/15 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="leading-tight">
              <p className="font-['Cabinet_Grotesk'] font-bold text-[15px]">Eva · asistentka EffectDeal</p>
              <p className="text-[11px] opacity-80 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-700 inline-block" /> Odpovídá hned
              </p>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="ml-auto w-8 h-8 rounded-full hover:bg-black/10 flex items-center justify-center" aria-label="Zavřít chat" data-testid="chat-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3.5 py-4 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] text-sm leading-relaxed whitespace-pre-wrap px-3.5 py-2.5 rounded-2xl ${
                    m.role === "user"
                      ? "bg-orange-500 text-black rounded-br-md"
                      : "bg-white/[0.06] text-zinc-100 border border-white/5 rounded-bl-md"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-white/[0.06] border border-white/5 rounded-2xl rounded-bl-md px-4 py-3 flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-zinc-400 animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-2 h-2 rounded-full bg-zinc-400 animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-2 h-2 rounded-full bg-zinc-400 animate-bounce" />
                </div>
              </div>
            )}
            {messages.length <= 1 && !loading && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => send(s)} data-testid="chat-suggestion"
                    className="text-xs text-orange-300 border border-orange-500/30 bg-orange-500/5 hover:bg-orange-500/15 rounded-full px-3 py-1.5 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Lead form */}
          {showLead && (
            <form onSubmit={submitLead} className="px-3.5 py-3 border-t border-white/10 bg-white/[0.02] space-y-2" data-testid="chat-lead-form">
              {leadDone ? (
                <p className="text-sm text-emerald-400 flex items-center gap-2 py-1"><Check className="w-4 h-4" /> Děkujeme, ozveme se vám!</p>
              ) : (
                <>
                  <p className="text-xs text-zinc-400">Nechte nám kontakt a ozveme se vám s nabídkou na míru.</p>
                  <input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Jméno (nepovinné)" data-testid="chat-lead-name"
                    className="w-full bg-[#121214] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-500 focus:border-orange-500 outline-none" />
                  <input value={lead.contact} onChange={(e) => setLead({ ...lead, contact: e.target.value })} placeholder="Telefon nebo e-mail *" data-testid="chat-lead-contact"
                    className="w-full bg-[#121214] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-500 focus:border-orange-500 outline-none" />
                  {leadErr && <p className="text-xs text-red-400" data-testid="chat-lead-error">{leadErr}</p>}
                  <div className="flex gap-2">
                    <button type="submit" disabled={leadSending} data-testid="chat-lead-submit"
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 text-black text-sm font-semibold py-2 disabled:opacity-60">
                      {leadSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Odeslat
                    </button>
                    <button type="button" onClick={() => setShowLead(false)} className="px-3 rounded-xl border border-white/10 text-sm text-zinc-400 hover:text-white">Zpět</button>
                  </div>
                </>
              )}
            </form>
          )}

          {/* Input */}
          {!showLead && (
            <div className="px-3 py-2.5 border-t border-white/10 bg-[#0b0b0d] shrink-0">
              <button type="button" onClick={() => { setLeadDone(false); setShowLead(true); }} data-testid="chat-leave-contact"
                className="w-full mb-2 text-xs text-orange-300 border border-orange-500/30 bg-orange-500/5 hover:bg-orange-500/15 rounded-full py-1.5 transition-colors">
                📞 Nechat kontakt – ozveme se vám
              </button>
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Napište zprávu…"
                  data-testid="chat-input"
                  className="flex-1 resize-none max-h-28 bg-[#121214] border border-white/10 rounded-2xl px-3.5 py-2.5 text-sm text-white placeholder-zinc-500 focus:border-orange-500 outline-none"
                />
                <button type="button" onClick={() => send()} disabled={loading || !input.trim()} data-testid="chat-send"
                  className="w-10 h-10 shrink-0 rounded-full bg-orange-500 text-black flex items-center justify-center disabled:opacity-40 hover:scale-105 active:scale-95 transition-transform">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
